import React, { useState } from 'react';
import { 
  Button, 
  IconButton, 
  Tooltip, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Stack
} from '@mui/material';
import { BookmarkAdd } from '@mui/icons-material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const AddToJournalButton = (props) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [questionNumber, setQuestionNumber] = useState('');
  const [ptNumber, setPtNumber] = useState('');
  const [sectionNumber, setSectionNumber] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuestionNumber('');
    setError(null);
  };


  const handleAddToJournal = async () => {
    if (!currentUser || !props.questionData) {
      setError('Missing required data');
      return;
    }
    
    if (!questionNumber.trim() || !ptNumber.trim() || !sectionNumber.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const now = new Date();
      const journalEntry = {
        userId: currentUser.uid,
        aiAnalysisId: props.selectedAnalysisId || props.questionData.aiAnalysisId,
        questionNumber: questionNumber.trim(),
        ptNumber: ptNumber.trim(),
        sectionNumber: sectionNumber.trim(),
        timestamp: serverTimestamp(),
        date: now,
        imageUrl: props.questionData.imageUrl,
        questionType: props.questionData.questionType,
        details: {
          aiAnalysis: {
            questionType: props.questionData.questionType,
            summary: props.questionData.summary,
            answerAnalysis: props.questionData.answerAnalysis
          },
          chatHistory: props.chatHistory || []
        },
        preview: {
          questionType: props.questionData.questionType,
          date: now.toISOString().split('T')[0]
        }
      };

      // Save to Firestore
      await addDoc(collection(db, 'wrongAnswerJournal'), journalEntry);
      setShowSuccess(true);
      handleClose();
      
      // Optional: Navigate to journal
      navigate('/app/journal');
    } catch (error) {
      console.error('Error adding to journal:', error);
      setError(error.message || 'Failed to add to journal');
    }
  };

  return (
    <>
      <Tooltip title="Add to Wrong Answer Journal">
        <IconButton
          color="primary"
          onClick={handleClickOpen}
          sx={{
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <BookmarkAdd />
        </IconButton>
      </Tooltip>

      {/* Dialog for question number input */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add to Wrong Answer Journal</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="PT Number"
              type="number"
              value={ptNumber}
              onChange={(e) => setPtNumber(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Section Number"
              type="number"
              value={sectionNumber}
              onChange={(e) => setSectionNumber(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Question Number"
              type="number"
              value={questionNumber}
              onChange={(e) => setQuestionNumber(e.target.value)}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleAddToJournal} variant="contained">
            Add to Journal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success notification */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Question added to journal successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddToJournalButton; 